import React, {
  TextareaHTMLAttributes,
  ChangeEvent,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useLayoutEffect,
} from 'react';
import classNames from 'classnames';
import { useFocusRing } from '@react-aria/focus';
import { AriaFocusProps } from '../../types/common';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import { Stack } from '../stack';
import { BaseText } from '../text';
import { semantic_colors } from '../../foundation';
import { textarea_base_css } from './styles';
import {
  getInputFontStyle,
  getInputPaddingBySize,
  input_default_style,
  input_disabled_style,
  input_entered_disabled_style,
  input_error_style,
  input_focus_style,
} from '../input/styles';
import { SizeProps, size, buildCSSWithLength } from '../../styles';
import { controlledInputValue } from '../input/InputBase';
import { getTextareaAutoSizeStyles, getTextareaComputedStyle } from './utils';
import { useResize } from '../../hooks/useResize';
import { css, CSSObject } from '@emotion/react';
import { TextareaSize } from '../input/types';

export interface AutoSizeType {
  minRows?: number;
  maxRows?: number;
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, SizeProps, AriaFocusProps {
  /** @default 'large' */
  size?: TextareaSize;
  error?: boolean;
  /**
   * 입력된 value count 노출 여부
   * @default false
   */
  showCounter?: boolean;
  /**
   * 입혁하는 텍스트에 따라 자동으로 크기를 조절하며 최소/최대 라인수를 설정할 수 있습니다.
   *  - height 또는 textarea 기본 rows 속성이 있을 경우 적용되지 않습니다.
   *  - autoSize를 사용할 경우 기본 resize는 none처리됩니다.
   *  - minRows(최소 행 수)의 기본값은 2입니다.
   *  - maxRows(최대 행 수)를 넘어갈 경우 자동 내부 스크롤 처리됩니다.
   * @default false
   */
  autoSize?: boolean | AutoSizeType;
}

function isElement(v: unknown): v is Element {
  return typeof Element !== 'undefined' && v instanceof Element;
}

const TEXTAREA_SMALL_PADDING = '6px 10px';

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      width,
      size: sizeProp = 'large',
      error,
      disabled,
      height,
      rows,
      value,
      maxLength,
      showCounter = false,
      unstableFocusVisible,
      autoSize = false,
      onChange,
      ...props
    },
    originRef,
  ) => {
    const { isFocusVisible, focusProps } = useFocusRing({ isTextInput: true });
    const [charLength, setCharLength] = useState<number>(0);
    const [autoSizeStyle, setAutoSizeStyle] = useState<CSSObject>();
    const { windowWidth, windowHeight } = useResize();
    const ref = React.useRef<HTMLTextAreaElement | null>(null);
    const textareaRef = mergeRefs(ref, originRef);
    const isControlled = value !== undefined;
    const isAllowAutoSize = !!autoSize && !height && !rows;
    const textareaPadding = sizeProp === 'small' ? TEXTAREA_SMALL_PADDING : getInputPaddingBySize(sizeProp);

    const [minRows, maxRows] = useMemo(() => {
      if (autoSize && typeof autoSize === 'object') {
        return [autoSize.minRows, autoSize.maxRows];
      }
      return [];
    }, [autoSize]);

    const status_style = {
      ...input_default_style,
      ...((unstableFocusVisible ?? isFocusVisible) && input_focus_style),
      ...(error && input_error_style),
      ...(disabled && input_disabled_style),
      ...(disabled && value && input_entered_disabled_style),
    };

    const autoResizeTextarea = () => {
      const node = ref.current;
      const nodeSizingData = node && isElement(node) && getTextareaComputedStyle(node);
      if (!nodeSizingData) {
        return;
      }
      const textareaAutoSizeStyle = getTextareaAutoSizeStyles(
        nodeSizingData,
        node.value || node.placeholder || '',
        minRows,
        maxRows,
      );
      setAutoSizeStyle(textareaAutoSizeStyle as CSSObject);
    };

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (!isControlled && isAllowAutoSize) {
          autoResizeTextarea();
        }
        const currentCharLength = controlledInputValue(e.target.value).length;
        const isUnderMaxLength = maxLength ? currentCharLength <= maxLength : true;

        if (isUnderMaxLength) {
          showCounter && setCharLength(currentCharLength);
          onChange?.(e);
        }
      },
      [onChange, maxLength, showCounter],
    );

    useEffect(() => {
      showCounter && setCharLength(controlledInputValue(value).length);
    }, [value]);

    useLayoutEffect(() => {
      if (isAllowAutoSize) {
        autoResizeTextarea();
      }
    }, [value, minRows, maxRows, isAllowAutoSize, windowWidth, windowHeight]);

    return (
      <Stack direction='column' width={width}>
        <textarea
          className={classNames('pds-input', className)}
          css={[
            textarea_base_css,
            css`
              ${buildCSSWithLength('padding', textareaPadding)}
            `,
            getInputFontStyle(sizeProp),
            status_style,
            ...(isAllowAutoSize ? [autoSizeStyle, buildCSSWithLength('width', width)] : [size({ width, height })]),
          ]}
          ref={textareaRef}
          value={value}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          onChange={handleChange}
          {...mergeProps(focusProps, props)}
        />
        {showCounter && (
          <BaseText
            kind='Body_12_Regular'
            alignment='trailing'
            color={error ? semantic_colors.state.negative : semantic_colors.content.secondary}
          >
            {maxLength ? `${charLength}/${maxLength}` : `${charLength}`}
          </BaseText>
        )}
      </Stack>
    );
  },
);
